import { useState } from "react";
import { ChapterTag, MaskedLine, Reveal } from "@/components/bits";
import Lightbox from "@/components/Lightbox";

const NOTES = [
  {
    n: "I",
    title: "A flowing arc",
    body: "Nine towers arranged in a single arc — stacked with ground-level greens, water bodies, and pedestrian networks, woven upwards across multiple levels.",
  },
  {
    n: "II",
    title: "Stepping to the lake",
    body: "The towers step down gradually from east to west, towards Tarahunise Lake — so every level holds its view as the form descends.",
  },
  {
    n: "III",
    title: "The Rooftop Ribbon",
    body: "A continuous walkway binding all nine towers — connecting amenities, wellness spaces, and social zones, with panoramic views across the site.",
  },
];

export default function Architecture() {
  const [siteOpen, setSiteOpen] = useState(false);
  return (
    <section id="architecture" className="py-24 md:py-36" data-testid="architecture-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <ChapterTag num="02" label="The Architecture" />
        <h2 className="font-display mt-12 max-w-3xl text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskedLine inView delay={0.05}>Nine towers,</MaskedLine>
          <MaskedLine inView delay={0.15}>
            <span className="italic text-[#064E3B]">one ribbon</span> in the sky.
          </MaskedLine>
        </h2>

        <div className="mt-16 grid grid-cols-1 gap-14 md:mt-20 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="space-y-10">
              {NOTES.map((note, i) => (
                <Reveal key={note.n} delay={i * 0.12} className="flex gap-8 border-t border-[#1C1917]/15 pt-8">
                  <span className="font-display text-2xl italic text-[#A98B5F]">{note.n}</span>
                  <div>
                    <h3 className="font-display text-2xl font-light tracking-tight md:text-3xl">{note.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#57534E] md:text-base">{note.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.4} className="mt-12">
              <button
                onClick={() => setSiteOpen(true)}
                data-testid="view-siteplan-btn"
                className="group text-[11px] font-semibold uppercase tracking-[0.28em] text-[#064E3B]"
              >
                <span className="border-b border-[#064E3B]/40 pb-1 transition-colors duration-300 group-hover:border-[#A98B5F] group-hover:text-[#A98B5F]">
                  View site plan
                </span>
              </button>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <div className="group overflow-hidden rounded-sm">
                <img
                  src="/images/ribbon.jpg"
                  alt="Resident walking the Rooftop Ribbon at sunset — a continuous walkway binding all nine towers"
                  data-testid="architecture-image"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 sm:aspect-[3/3.4] lg:aspect-[3/3.2]"
                />
              </div>
              <p className="mt-4 text-xs uppercase tracking-[0.24em] text-[#57534E]">
                The Rooftop Ribbon at dusk
              </p>
            </Reveal>
          </div>
        </div>
      </div>
      <Lightbox
        open={siteOpen}
        onClose={() => setSiteOpen(false)}
        src="/images/siteplan.jpg"
        alt="Site plan of South Reserve showing the ribbon and terrace amenity zone, anchor tree and clubhouse"
        caption="South Reserve — site plan"
      />
    </section>
  );
}
